import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, tokenExpired } from '../redux/actions/authActions';
import { accountStatusChecker } from '../utils/accountStatusChecker';
import { usePostHogIdentification } from '../hooks/usePostHogIdentification';

// Token validation utility
const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp > currentTime;
  } catch (error) {
    return false;
  }
};

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  // Identify user in PostHog when authenticated
  usePostHogIdentification(user, isAuthenticated);

  useEffect(() => {
    // Check if clearSession parameter is present
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('clearSession') === 'true') {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');
      // Remove the parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Check for stored auth data on app load
    const token = sessionStorage.getItem('authToken');
    const storedUser = sessionStorage.getItem('user');

    if (token && storedUser) {
      // Validate token before setting user
      if (isTokenValid(token)) {
        try {
          const parsedUser = JSON.parse(storedUser);
          dispatch(setUser(parsedUser));
          // Start account status checking when user is authenticated
          accountStatusChecker.startChecking();
        } catch (error) {
          // Clear invalid data
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('user');
          dispatch(tokenExpired());
        }
      } else {
        // Token is expired
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
        dispatch(tokenExpired());
      }
    }

    // Cleanup on unmount
    return () => {
      accountStatusChecker.stopChecking();
    };
  }, [dispatch]);

  return children;
};

export default AuthProvider;